import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import { setAdminMode } from '../../redux/slices/adminModeSlice';

const AdminPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSuperUser = useSelector((state) => state.isAdmin.isSuperUser);
  const adminMode = useSelector((state) => state.adminMode.adminMode);

  const [viewMode, setViewMode] = useState('admin');
  const [adminAction, setAdminAction] = useState('');
  const [userAction, setUserAction] = useState('');
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingUserId, setSavingUserId] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [newAccessIdentifier, setNewAccessIdentifier] = useState('');
  const [newAccessDays, setNewAccessDays] = useState(60);
  const [adminIdentifier, setAdminIdentifier] = useState('');
  const [bonusIdentifier, setBonusIdentifier] = useState('');
  const [bonusAmount, setBonusAmount] = useState(100);
  const [passwordIdentifier, setPasswordIdentifier] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [batchIdentifiers, setBatchIdentifiers] = useState('');
  const [batchDays, setBatchDays] = useState(60);
  const [batchBonusAmount, setBatchBonusAmount] = useState(100);
  const [revokeAllConfirmText, setRevokeAllConfirmText] = useState('');
  const [reportRows, setReportRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  const activeAdminShortcut = useMemo(() => {
    if (viewMode === 'users') {
      return 'users';
    }
    if (adminAction === 'report') {
      return 'report';
    }
    return adminAction;
  }, [viewMode, adminAction]);

  const hasUsers = useMemo(() => users.length > 0, [users]);
  const hasReports = useMemo(() => reportRows.length > 0, [reportRows]);

  const parseBatchIdentifiers = useCallback((rawText) => {
    return String(rawText || '')
      .split(/[\n,;\t ]+/g)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => ({ userIdentifier: item }));
  }, []);

  const loadUsers = useCallback(async (searchQuery = '') => {
    setLoading(true);
    setError('');
    setInfo('');

    try {
      const { data } = await axiosInstance.post('/admin/users/search', {
        query: searchQuery,
        page: 1,
        limit: 40
      });

      if (data.success) {
        setUsers(data.data || []);
      } else {
        setUsers([]);
      }
    } catch (err) {
      setUsers([]);
      setError(err.response?.data?.error || 'Не удалось загрузить пользователей');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAdmins = useCallback(async () => {
    try {
      const { data } = await axiosInstance.post('/admin/superusers/list');
      if (data.success) {
        setAdmins(data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось загрузить список админов');
    }
  }, []);

  useEffect(() => {
    if (isSuperUser) {
      loadUsers('');
      loadAdmins();
    }
  }, [isSuperUser, loadUsers, loadAdmins]);

  const handleSearch = async (event) => {
    event.preventDefault();
    await loadUsers(query.trim());
  };

  const updateAccess = async ({ userIdentifier, grantAccess, subscriptionDays = 0 }) => {
    setSavingUserId(userIdentifier);
    setError('');
    setInfo('');

    try {
      const { data } = await axiosInstance.post('/admin/users/set-access', {
        userIdentifier,
        grantAccess,
        subscriptionDays
      });

      if (data.success && data.data) {
        setUsers((prev) => {
          const index = prev.findIndex((user) => user.userId === data.data.userId);
          if (index === -1) {
            return [data.data, ...prev];
          }

          const next = [...prev];
          next[index] = data.data;
          return next;
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось изменить доступ');
    } finally {
      setSavingUserId('');
    }
  };

  const applyBonus = async ({ userIdentifier, amount }) => {
    const normalized = String(userIdentifier || '').trim();
    if (!normalized) {
      setError('Выберите пользователя или введите ID/username/email');
      return;
    }

    if (!Number(amount)) {
      setError('Укажите корректное количество баллов');
      return;
    }

    setSavingUserId(normalized);
    setError('');
    setInfo('');

    try {
      const { data } = await axiosInstance.post('/admin/users/bonus', {
        userIdentifier: normalized,
        amount: Number(amount)
      });

      if (data.success && data.data) {
        setUsers((prev) => {
          const index = prev.findIndex((user) => user.userId === data.data.userId);
          if (index === -1) {
            return [data.data, ...prev];
          }

          const next = [...prev];
          next[index] = data.data;
          return next;
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось начислить баллы');
    } finally {
      setSavingUserId('');
    }
  };

  const applyBatchAction = async ({ action, entries }) => {
    if (!entries || entries.length === 0) {
      setError('Добавьте хотя бы один идентификатор');
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');

    try {
      const { data } = await axiosInstance.post('/admin/users/batch', {
        action,
        entries
      });

      if (data.success) {
        const summary = data.summary || {};
        setInfo(`Выполнено: ${summary.success || 0}, ошибок: ${summary.failed || 0}`);
        await loadUsers(query.trim());
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось выполнить массовую операцию');
    } finally {
      setLoading(false);
    }
  };

  const addSuperUser = async (event) => {
    event.preventDefault();
    const userIdentifier = adminIdentifier.trim();
    if (!userIdentifier) {
      setError('Введите ID/username/email админа');
      return;
    }

    setSavingUserId(userIdentifier);
    setError('');
    setInfo('');
    try {
      const { data } = await axiosInstance.post('/admin/superusers/add', { userIdentifier });
      if (data.success) {
        setInfo('Админ добавлен');
        setAdminIdentifier('');
        await loadAdmins();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось добавить админа');
    } finally {
      setSavingUserId('');
    }
  };

  const removeSuperUser = async (userIdentifier) => {
    const normalized = String(userIdentifier || '').trim();
    if (!normalized) {
      return;
    }

    setSavingUserId(normalized);
    setError('');
    setInfo('');
    try {
      const { data } = await axiosInstance.post('/admin/superusers/remove', { userIdentifier: normalized });
      if (data.success) {
        setInfo('Админ удален');
        await loadAdmins();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось удалить админа');
    } finally {
      setSavingUserId('');
    }
  };

  const resetUserPassword = async (event) => {
    event.preventDefault();

    const userIdentifier = String(passwordIdentifier || '').trim();
    if (!userIdentifier) {
      setError('Введите username/ID пользователя');
      return;
    }

    if (String(newPassword || '').length < 6) {
      setError('Новый пароль должен быть минимум 6 символов');
      return;
    }

    setSavingUserId(userIdentifier);
    setError('');
    setInfo('');
    try {
      const { data } = await axiosInstance.post('/admin/users/reset-password', {
        userIdentifier,
        newPassword
      });
      if (data.success) {
        setInfo('Пароль обновлен');
        setPasswordIdentifier('');
        setNewPassword('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось сменить пароль');
    } finally {
      setSavingUserId('');
    }
  };

  const loadParticipantsReport = async () => {
    setReportLoading(true);
    setError('');
    setInfo('');

    try {
      const { data } = await axiosInstance.post('/admin/reports/participants', {
        query: query.trim(),
        page: 1,
        limit: 50
      });

      if (data.success) {
        setReportRows(data.data || []);
      } else {
        setReportRows([]);
      }
    } catch (err) {
      setReportRows([]);
      setError(err.response?.data?.error || 'Не удалось загрузить отчет');
    } finally {
      setReportLoading(false);
    }
  };

  const handleGrantNewUser = async (event) => {
    event.preventDefault();

    const userIdentifier = newAccessIdentifier.trim();
    if (!userIdentifier) {
      setError('Введите ID/username/email ученика');
      return;
    }

    await updateAccess({
      userIdentifier,
      grantAccess: true,
      subscriptionDays: Number(newAccessDays) || 0
    });

    setNewAccessIdentifier('');
    await loadUsers(query.trim());
  };

  const openLessonsAdmin = () => {
    dispatch(setAdminMode(true));
    navigate('/');
  };

  const openFaqAdmin = () => {
    dispatch(setAdminMode(true));
    navigate('/faq');
  };

  const openGiftsAdmin = () => {
    dispatch(setAdminMode(true));
    navigate('/gifts');
  };

  const disableAdminMode = () => {
    dispatch(setAdminMode(false));
  };

  const renderAdminPanelButtons = () => (
    <div className="admin-shortcuts">
      <button
        type="button"
        className={`admin-shortcut ${activeAdminShortcut === 'add-admin' ? 'active' : ''}`}
        onClick={() => setAdminAction('add-admin')}
      >
        ➕ Добавить админа
      </button>
      <button
        type="button"
        className={`admin-shortcut ${activeAdminShortcut === 'remove-admin' ? 'active' : ''}`}
        onClick={() => setAdminAction('remove-admin')}
      >
        ❌ Удалить админа
      </button>
      <button
        type="button"
        className={`admin-shortcut ${activeAdminShortcut === 'users' ? 'active' : ''}`}
        onClick={() => { setViewMode('users'); setUserAction('add-user'); }}
      >
        👥 Менеджмент юзеров
      </button>
      <button type="button" className="admin-shortcut" onClick={() => setInfo('Раздел рассылок пока доступен в Telegram-админке')}>
        📨 Создать рассылку
      </button>
      <button type="button" className="admin-shortcut" onClick={() => setInfo('Список рассылок пока доступен в Telegram-админке')}>
        📋 Список рассылок
      </button>
      <button
        type="button"
        className={`admin-shortcut ${activeAdminShortcut === 'report' ? 'active' : ''}`}
        onClick={() => { setAdminAction('report'); loadParticipantsReport(); }}
      >
        📊 Отчёт
      </button>
    </div>
  );

  const renderUserManagementButtons = () => (
    <div className="admin-shortcuts">
      <button type="button" className={`admin-shortcut ${userAction === 'add-user' ? 'active' : ''}`} onClick={() => setUserAction('add-user')}>👤 Добавить пользователя</button>
      <button type="button" className={`admin-shortcut ${userAction === 'batch-add' ? 'active' : ''}`} onClick={() => setUserAction('batch-add')}>📦 Массовое добавление</button>
      <button type="button" className={`admin-shortcut ${userAction === 'extend' ? 'active' : ''}`} onClick={() => setUserAction('extend')}>🔄 Начислить подписку</button>
      <button type="button" className={`admin-shortcut ${userAction === 'batch-extend' ? 'active' : ''}`} onClick={() => setUserAction('batch-extend')}>📦 Массовые подписки</button>
      <button type="button" className={`admin-shortcut ${userAction === 'bonus' ? 'active' : ''}`} onClick={() => setUserAction('bonus')}>💰 Начислить бонусы</button>
      <button type="button" className={`admin-shortcut ${userAction === 'batch-bonus' ? 'active' : ''}`} onClick={() => setUserAction('batch-bonus')}>📦 Массовое начисление</button>
      <button type="button" className={`admin-shortcut ${userAction === 'revoke' ? 'active' : ''}`} onClick={() => setUserAction('revoke')}>❌ Снять подписку</button>
      <button type="button" className={`admin-shortcut ${userAction === 'batch-revoke' ? 'active' : ''}`} onClick={() => setUserAction('batch-revoke')}>📦 Массовое снятие</button>
      <button type="button" className={`admin-shortcut ${userAction === 'reset-password' ? 'active' : ''}`} onClick={() => setUserAction('reset-password')}>🔐 Сменить пароль</button>
      <button type="button" className="admin-shortcut danger" onClick={() => setUserAction('revoke-all')}>🚫 Снять всем подписки</button>
      <button type="button" className="admin-shortcut" onClick={() => setViewMode('admin')}>🔙 Назад в админ-панель</button>
    </div>
  );

  const renderActionPanel = () => {
    if (viewMode === 'admin') {
      if (adminAction === 'add-admin') {
        return (
          <section className="admin-users-section">
            <h2>Добавить админа</h2>
            <form className="admin-grant-form" onSubmit={addSuperUser}>
              <input
                type="text"
                value={adminIdentifier}
                onChange={(e) => setAdminIdentifier(e.target.value)}
                placeholder="Введите username администратора"
              />
              <button type="submit" disabled={savingUserId === adminIdentifier.trim()}>Добавить</button>
            </form>
          </section>
        );
      }

      if (adminAction === 'remove-admin') {
        return (
          <section className="admin-users-section">
            <h2>Удалить админа</h2>
            <div className="admin-users-table-wrap">
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>Имя</th>
                    <th>Username</th>
                    <th>Telegram ID</th>
                    <th>Действие</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => {
                    const adminIdentifierValue = admin.userId || admin.telegramID || admin.username;
                    return (
                      <tr key={admin.id}>
                        <td>{admin.name || 'Аноним'}</td>
                        <td>{admin.username || '-'}</td>
                        <td>{admin.telegramID || '-'}</td>
                        <td>
                          <button
                            type="button"
                            disabled={savingUserId === adminIdentifierValue}
                            onClick={() => removeSuperUser(adminIdentifierValue)}
                          >
                            Удалить
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      }

      if (adminAction === 'report') {
        return (
          <section className="admin-users-section">
            <h2>Отчеты по участникам</h2>
            <div className="admin-actions-inline" style={{ marginBottom: 12 }}>
              <button type="button" onClick={loadParticipantsReport} disabled={reportLoading}>
                {reportLoading ? 'Формирование...' : 'Обновить отчет'}
              </button>
            </div>
            {reportLoading ? <div className="admin-status">Загрузка отчета...</div> : null}
            {!reportLoading && !hasReports ? <div className="admin-status">Нет данных отчета</div> : null}
            {hasReports && (
              <div className="admin-users-table-wrap">
                <table className="admin-users-table">
                  <thead>
                    <tr>
                      <th>Участник</th>
                      <th>Тип</th>
                      <th>Доступ</th>
                      <th>Уроков завершено</th>
                      <th>Чатов</th>
                      <th>Баллы уроков</th>
                      <th>Бонусы</th>
                      <th>Сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportRows.map((row) => {
                      const rowKey = row.userId || row.telegramID || row.username;
                      return (
                        <tr key={rowKey}>
                          <td>{row.name || row.username || row.telegramID || '-'}</td>
                          <td>{row.accountType || '-'}</td>
                          <td>{row.isAccepted ? 'Да' : 'Нет'}</td>
                          <td>{row.completedLessonsCount || 0}</td>
                          <td>{row.chatsCount || 0}</td>
                          <td>{row.totalLessonScore || 0}</td>
                          <td>{row.bonusScore || 0}</td>
                          <td>{row.totalScore || 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      }

      return null;
    }

    return (
      <section className="admin-users-section">
        <h2>Операции с пользователями</h2>

        <form className="admin-search-form" onSubmit={handleSearch}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск по имени, username, email или ID"
          />
          <button type="submit" disabled={loading}>Найти</button>
          <button type="button" onClick={() => { setQuery(''); loadUsers(''); }} disabled={loading}>Сброс</button>
        </form>

        {(userAction === 'add-user' || userAction === 'extend') && (
          <form className="admin-grant-form" onSubmit={handleGrantNewUser}>
            <input
              type="text"
              value={newAccessIdentifier}
              onChange={(e) => setNewAccessIdentifier(e.target.value)}
              placeholder="Telegram ID, user ID, username или email"
            />
            <input
              type="number"
              min="1"
              value={newAccessDays}
              onChange={(e) => setNewAccessDays(e.target.value)}
              placeholder="Дней подписки"
            />
            <button type="submit" disabled={savingUserId === newAccessIdentifier.trim()}>
              {userAction === 'add-user' ? 'Добавить пользователя' : 'Начислить подписку'}
            </button>
          </form>
        )}

        {(userAction === 'batch-add' || userAction === 'batch-extend' || userAction === 'batch-revoke') && (
          <div className="admin-bonus-box">
            <h3>Массовая операция</h3>
            <textarea
              className="admin-textarea"
              value={batchIdentifiers}
              onChange={(e) => setBatchIdentifiers(e.target.value)}
              placeholder="Вставьте список ID/username/email через пробел, запятую или новую строку"
            />

            {userAction !== 'batch-revoke' && (
              <input
                type="number"
                min="1"
                value={batchDays}
                onChange={(e) => setBatchDays(e.target.value)}
                placeholder="Дней подписки"
              />
            )}

            <button
              type="button"
              onClick={() => applyBatchAction({
                action: userAction === 'batch-revoke' ? 'revoke' : userAction === 'batch-add' ? 'grant' : 'extend',
                entries: parseBatchIdentifiers(batchIdentifiers).map((entry) => ({
                  ...entry,
                  subscriptionDays: Number(batchDays) || 60
                }))
              })}
            >
              Запустить массовую операцию
            </button>
          </div>
        )}

        {(userAction === 'bonus') && (
          <div className="admin-bonus-box">
            <h3>Начисление баллов</h3>
            <form
              className="admin-grant-form"
              onSubmit={async (event) => {
                event.preventDefault();
                await applyBonus({ userIdentifier: bonusIdentifier, amount: bonusAmount });
                setBonusIdentifier('');
              }}
            >
              <input
                type="text"
                value={bonusIdentifier}
                onChange={(e) => setBonusIdentifier(e.target.value)}
                placeholder="Telegram ID, user ID, username или email"
              />
              <input
                type="number"
                value={bonusAmount}
                onChange={(e) => setBonusAmount(e.target.value)}
                placeholder="Количество баллов"
              />
              <button type="submit" disabled={savingUserId === bonusIdentifier.trim()}>Начислить</button>
            </form>
            <div className="admin-actions-inline">
              {[10, 50, 100, 200, 500, 1000].map((value) => (
                <button key={value} type="button" onClick={() => setBonusAmount(value)}>{value} б.</button>
              ))}
            </div>
          </div>
        )}

        {(userAction === 'batch-bonus') && (
          <div className="admin-bonus-box">
            <h3>Массовое начисление бонусов</h3>
            <textarea
              className="admin-textarea"
              value={batchIdentifiers}
              onChange={(e) => setBatchIdentifiers(e.target.value)}
              placeholder="Вставьте список ID/username/email через пробел, запятую или новую строку"
            />
            <input
              type="number"
              value={batchBonusAmount}
              onChange={(e) => setBatchBonusAmount(e.target.value)}
              placeholder="Сколько баллов начислить каждому"
            />
            <button
              type="button"
              onClick={() => applyBatchAction({
                action: 'bonus',
                entries: parseBatchIdentifiers(batchIdentifiers).map((entry) => ({
                  ...entry,
                  amount: Number(batchBonusAmount) || 100
                }))
              })}
            >
              Начислить массово
            </button>
          </div>
        )}

        {(userAction === 'revoke') && (
          <form
            className="admin-grant-form"
            onSubmit={async (event) => {
              event.preventDefault();
              const userIdentifier = newAccessIdentifier.trim();
              if (!userIdentifier) {
                setError('Введите ID/username/email ученика');
                return;
              }
              await updateAccess({ userIdentifier, grantAccess: false, subscriptionDays: 0 });
              setNewAccessIdentifier('');
              await loadUsers(query.trim());
            }}
          >
            <input
              type="text"
              value={newAccessIdentifier}
              onChange={(e) => setNewAccessIdentifier(e.target.value)}
              placeholder="Telegram ID, user ID, username или email"
            />
            <button type="submit" disabled={savingUserId === newAccessIdentifier.trim()}>Снять подписку</button>
          </form>
        )}

        {(userAction === 'reset-password') && (
          <div className="admin-bonus-box">
            <h3>Смена пароля пользователя</h3>
            <form className="admin-grant-form" onSubmit={resetUserPassword}>
              <input
                type="text"
                value={passwordIdentifier}
                onChange={(e) => setPasswordIdentifier(e.target.value)}
                placeholder="Username / Telegram ID / user ID"
              />
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Новый пароль"
              />
              <button type="submit" disabled={savingUserId === passwordIdentifier.trim()}>Обновить пароль</button>
            </form>
            <div className="admin-status">Старый пароль будет перезаписан в базе.</div>
          </div>
        )}

        {(userAction === 'revoke-all') && (
          <div className="admin-bonus-box">
            <h3>Снять подписку у всех</h3>
            <p className="admin-status">Для безопасности введите подтверждение: REVOKE_ALL</p>
            <input
              type="text"
              value={revokeAllConfirmText}
              onChange={(e) => setRevokeAllConfirmText(e.target.value)}
              placeholder="REVOKE_ALL"
            />
            <button
              type="button"
              className="admin-shortcut danger"
              onClick={async () => {
                setLoading(true);
                setError('');
                setInfo('');
                try {
                  const { data } = await axiosInstance.post('/admin/users/revoke-all', {
                    confirmText: revokeAllConfirmText.trim()
                  });
                  if (data.success) {
                    setInfo(`Готово. Пользователей обновлено: ${data.data.usersUpdated}`);
                    setRevokeAllConfirmText('');
                    await loadUsers(query.trim());
                  }
                } catch (err) {
                  setError(err.response?.data?.error || 'Не удалось выполнить массовое снятие');
                } finally {
                  setLoading(false);
                }
              }}
            >
              Подтвердить снятие подписок
            </button>
          </div>
        )}

        {loading ? <div className="admin-status">Загрузка...</div> : null}
        {!loading && !hasUsers ? <div className="admin-status">Пользователи не найдены</div> : null}

        {hasUsers && (
          <div className="admin-users-table-wrap">
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th>Имя</th>
                  <th>Username/email</th>
                  <th>Telegram ID</th>
                  <th>Доступ</th>
                  <th>Подписка до</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const rowIdentifier = user.userId || user.telegramID || user.username;
                  const isSaving = savingUserId === rowIdentifier;
                  return (
                    <tr key={rowIdentifier}>
                      <td>{user.name || 'Аноним'}</td>
                      <td>{user.username || '-'}</td>
                      <td>{user.telegramID || '-'}</td>
                      <td>{user.isAccepted ? 'Активен' : 'Нет'}</td>
                      <td>{user.expireDate ? new Date(user.expireDate).toLocaleDateString('ru-RU') : '-'}</td>
                      <td>
                        <div className="admin-actions-inline">
                          <button type="button" disabled={isSaving} onClick={() => updateAccess({ userIdentifier: rowIdentifier, grantAccess: true, subscriptionDays: 60 })}>+60 дней</button>
                          <button type="button" disabled={isSaving} onClick={() => updateAccess({ userIdentifier: rowIdentifier, grantAccess: !user.isAccepted, subscriptionDays: user.isAccepted ? 0 : 60 })}>{user.isAccepted ? 'Отключить' : 'Включить'}</button>
                          <button type="button" disabled={isSaving} onClick={() => applyBonus({ userIdentifier: rowIdentifier, amount: 100 })}>+100 б.</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    );
  };

  if (!isSuperUser) {
    return (
      <div className="admin-page-container">
        <h1 className="admin-page-title">Доступ запрещен</h1>
        <p className="admin-page-subtitle">Этот раздел доступен только администраторам.</p>
      </div>
    );
  }

  return (
    <div className="admin-page-container">
      <div className="admin-top">
        <h1 className="admin-page-title">Веб-админка</h1>
        <p className="admin-page-subtitle">Управление уроками, контентом и доступом пользователей</p>
        <div className="admin-stats-strip">
          <div className="admin-stat-item">
            <span className="admin-stat-label">Админов</span>
            <strong>{admins.length}</strong>
          </div>
          <div className="admin-stat-item">
            <span className="admin-stat-label">Пользователей в выдаче</span>
            <strong>{users.length}</strong>
          </div>
          <div className="admin-stat-item">
            <span className="admin-stat-label">Режим</span>
            <strong>{viewMode === 'admin' ? 'Панель' : 'Менеджмент'}</strong>
          </div>
        </div>
      </div>

      <div className="admin-toolbar">
        <button type="button" className="admin-shortcut" onClick={openLessonsAdmin}>🎬 Видео уроков</button>
        <button type="button" className="admin-shortcut" onClick={openFaqAdmin}>❓ FAQ</button>
        <button type="button" className="admin-shortcut" onClick={openGiftsAdmin}>🎁 Подарки</button>
        <button type="button" className="admin-shortcut danger" onClick={disableAdminMode}>⛔ Выключить admin mode</button>
      </div>

      <div className="admin-panel-hint">
        {viewMode === 'admin'
          ? 'Главное меню как в Telegram: выберите раздел для действия.'
          : 'Менеджмент юзеров: выберите операцию, затем выполните действие в форме ниже.'}
      </div>

      {viewMode === 'admin' ? renderAdminPanelButtons() : renderUserManagementButtons()}
      {error ? <div className="admin-error">{error}</div> : null}
      {info ? <div className="admin-status">{info}</div> : null}
      {renderActionPanel()}

      <div className="admin-mode-indicator">
        Текущий admin mode: {adminMode ? 'включен' : 'выключен'}
      </div>
    </div>
  );
};

export default AdminPage;
